/**
 * Cart upsell component — handles AJAX add-to-cart for upsell products
 * and dispatches a cart:update event so the section re-renders and
 * removes the added product from the upsell grid automatically.
 */
class CartUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleVariantChange);
  }

  /**
   * When a variant <select> changes, update the card's data-variant-id
   * so the add button uses the correct variant.
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const select = /** @type {HTMLSelectElement | null} */ (
      event.target instanceof HTMLSelectElement ? event.target : null
    );
    if (!('upsellVariantSelect' in (select?.dataset ?? {}))) return;

    const card = select.closest('[data-upsell-card]');
    if (!(card instanceof HTMLElement)) return;

    // Update active variant id for add-to-cart
    card.dataset.variantId = select.value;

    // Update displayed price from the selected option's data-price attribute
    const selectedOption = select.options[select.selectedIndex];
    const newPrice = selectedOption?.dataset.price;
    if (newPrice) {
      const priceEl = card.querySelector('.cart-upsell__price');
      if (priceEl) priceEl.textContent = newPrice;
    }
  };

  /**
   * When an "ADD" button is clicked, add the product to cart via AJAX.
   * @param {Event} event
   */
  #handleClick = async (event) => {
    const btn = /** @type {HTMLElement | null} */ (
      (event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null)
    );
    if (!(btn instanceof HTMLButtonElement)) return;

    const card = btn.closest('[data-upsell-card]');
    if (!(card instanceof HTMLElement)) return;

    // Prefer variant from select, fallback to card attribute
    const select = card.querySelector('select[data-upsell-variant-select]');
    const variantId = (select instanceof HTMLSelectElement ? select.value : null) || card.dataset.variantId;

    if (!variantId) return;

    btn.setAttribute('data-loading', '');
    btn.disabled = true;

    try {
      // Collect section IDs from all cart-items-components so Shopify
      // returns fresh section HTML that we can morph into the DOM.
      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          sectionIds.push(el.dataset.sectionId);
        }
      });

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }

      const cartAddUrl =
        (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        // Shopify returned an error (e.g. sold out, quantity limit)
        console.error('Cart upsell error:', data.description || data.message);
        return;
      }

      // Dispatch cart:update so CartItemsComponent morphs the section HTML.
      // This causes the Liquid template to re-render without the added product
      // in the upsell grid.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'cart-upsell-component',
            data: {
              sections: data.sections || {},
              source: 'cart-upsell-component',
              itemCount: 1,
            },
          },
        })
      );
    } catch (error) {
      console.error('Cart upsell add failed:', error);
    } finally {
      btn.removeAttribute('data-loading');
      btn.disabled = false;
    }
  };
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
